// Loan Recovery — a staff member pays a Staff Loan or a Salary Advance back in
// cash or bank (outside payroll). Posting the voucher records
// Dr Cash/Bank, Cr Staff Account and lowers the loans' / advances' balances
// (utils/loanRecoveryHelper.js). A loan paid off closes and payroll stops
// deducting it; a part payment keeps the same monthly installment, so the loan
// simply finishes sooner.
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  FiPlus, FiEdit2, FiTrash2, FiSearch, FiArrowLeft,
  FiCheck, FiXCircle, FiEye, FiPrinter
} from 'react-icons/fi';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import PageLayout from '../../components/PageLayout';
import { useAuth } from '../../context/AuthContext';
import { db } from '../../config/firebase';
import { colors, spacing, borderRadius, typography } from '../../theme/colors';
import {
  ActionBar, SearchBox, FilterGroup, StatusFilterSelect, TranAddButton,
  LoadingMessage, TranEmptyState, TableContainer, Table, Th, Tr, Td,
  StatusBadge, ActionGroup, IconBtn,
  FormHeader, BackButton, FormTitle, FormActions, SaveButton, FormCard,
  SectionTitle, FormGrid, FormGroup, Label, Input, Select, TextArea
} from '../../components/SharedStyles';
import {
  createTransaction, updateTransaction, deleteTransaction,
  getTransactions, updateTransactionStatus
} from '../../utils/transactionHelper';
import { printDocument, loadDocumentFormats } from '../../utils/printTemplates';
import {
  LOAN_RECOVERY_CODE, loadOpenBalancesForStaff, countedAllocations,
  checkAllocations, syncRecoveryEffects
} from '../../utils/loanRecoveryHelper';
import useDocumentShortcuts from '../../hooks/useDocumentShortcuts';
import usePagePermissions from '../../hooks/usePagePermissions';
import useCurrency from '../../hooks/useCurrency';
import useOpenFromReport from '../../hooks/useOpenFromReport';
import { isFutureDate } from '../../utils/validationHelpers';

const STATUS_COLORS = {
  Draft: colors.warning.main,
  Posted: colors.success.main,
  Cancelled: colors.error.main,
};

const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const today = () => new Date().toISOString().split('T')[0];
const EMPTY_FORM = {
  docDate: today(),
  partyId: '', partyName: '', partyAccountId: '', partyAccountCode: '',
  paymentMethod: 'cash',
  paymentAccountId: '', paymentAccountCode: '', paymentAccountName: '',
  reference: '', notes: '',
  status: 'Draft'
};
const kindLabel = (code) => (code === 'ADV' ? 'Salary Advance' : 'Staff Loan');

const LoanRecovery = () => {
  const { activeCompany, activeFinancialYear, currentUser, activeBranch } = useAuth();
  const { canEdit, canDelete } = usePagePermissions('salary', 'loanRecovery');
  const { formatCurrency } = useCurrency();
  const location = useLocation();
  const navigate = useNavigate();
  const showBranchCol = activeBranch && activeBranch.id === 'all';

  const [viewMode, setViewMode] = useState('list');
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');

  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({ ...EMPTY_FORM, docDate: today() });
  // The staff's loans / advances with what is left on them, and the amount
  // received against each now.
  const [rows, setRows] = useState([]);
  const [rowsLoading, setRowsLoading] = useState(false);

  const [staffList, setStaffList] = useState([]);
  const [cashAccounts, setCashAccounts] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);

  const [printConfig, setPrintConfig] = useState(null);
  useEffect(() => {
    if (activeCompany?.id) loadDocumentFormats(activeCompany.id).then(setPrintConfig);
  }, [activeCompany]);
  const handlePrint = (v) => printDocument('loanRecovery', v, activeCompany, printConfig, activeBranch);

  // ─── Load data ───
  const loadVouchers = useCallback(async () => {
    if (!activeCompany?.id) return;
    setLoading(true);
    try {
      const txs = await getTransactions(activeCompany.id, {
        tranTypeCode: LOAN_RECOVERY_CODE,
        ...(activeBranch && activeBranch.id !== 'all' ? { branchId: activeBranch.id } : {})
      });
      setVouchers(txs);
    } catch (e) { console.error('Load LRC error:', e); }
    finally { setLoading(false); }
  }, [activeCompany, activeBranch]);

  const loadLookups = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const [staffSnap, accSnap] = await Promise.all([
        getDocs(query(collection(db, 'companies', activeCompany.id, 'salaryStaff'), orderBy('staffCode'))),
        getDocs(collection(db, 'companies', activeCompany.id, 'accounts'))
      ]);
      setStaffList(staffSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      const allAccs = accSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      setCashAccounts(allAccs.filter(a => a.level === 5 && a.code?.startsWith('11101') && a.isActive !== false)
        .sort((a, b) => (a.code || '').localeCompare(b.code || '')));
      setBankAccounts(allAccs.filter(a => a.level === 5 && a.code?.startsWith('11102') && a.isActive !== false)
        .sort((a, b) => (a.code || '').localeCompare(b.code || '')));
    } catch (e) { console.error('Load lookups error:', e); }
  }, [activeCompany]);

  useEffect(() => { loadVouchers(); loadLookups(); }, [loadVouchers, loadLookups]);

  // The staff's open loans / advances. When editing a posted voucher, what it
  // already counts is available again (and a loan it closed is listed too).
  const loadRows = useCallback(async (staffId, voucher = null, fillId = null) => {
    if (!staffId || !activeCompany?.id) { setRows([]); return; }
    setRowsLoading(true);
    try {
      const open = await loadOpenBalancesForStaff(activeCompany.id, staffId);
      const counted = countedAllocations(voucher);
      const given = {};
      (voucher?.allocations || []).forEach(a => { given[a.transactionId] = a; });
      const list = open.map(o => ({
        ...o,
        available: r2(o.outstanding + (counted[o.transactionId] || 0)),
        amount: given[o.transactionId] ? r2(given[o.transactionId].amount) : 0,
      }));
      (voucher?.allocations || []).forEach(a => {
        if (list.some(x => x.transactionId === a.transactionId)) return;
        list.push({
          transactionId: a.transactionId, tranTypeCode: a.tranTypeCode, docNumber: a.docNumber,
          docDate: a.docDate || '', totalAmount: 0,
          available: r2(counted[a.transactionId] || 0), amount: r2(a.amount),
        });
      });
      if (fillId) list.forEach(x => { if (x.transactionId === fillId) x.amount = x.available; });
      setRows(list);
    } catch (e) {
      console.error('Load balances error:', e);
      setRows([]);
    } finally { setRowsLoading(false); }
  }, [activeCompany]);

  const total = useMemo(() => r2(rows.reduce((s, r) => s + (Number(r.amount) || 0), 0)), [rows]);
  const readOnly = !!editing && editing.status === 'Cancelled';

  // ─── Form helpers ───
  const resetForm = () => {
    setEditing(null);
    setFormData({ ...EMPTY_FORM, docDate: today() });
    setRows([]);
  };

  const pickStaff = (staffId, fillId = null) => {
    const s = staffList.find(x => x.id === staffId);
    setFormData(prev => ({
      ...prev,
      partyId: staffId,
      partyName: s?.fullName || '',
      partyAccountId: s?.linkedAccountId || '',
      partyAccountCode: ''
    }));
    loadRows(staffId, null, fillId);
  };

  const openNew = (preset = null) => {
    if (!activeFinancialYear?.id) { alert('Activate a financial year first.'); return; }
    if (activeBranch && activeBranch.id === 'all') { alert('Pick a specific branch before creating a Loan Recovery.'); return; }
    resetForm();
    setViewMode('form');
    if (preset?.staffId) pickStaff(preset.staffId, preset.transactionId || null);
  };

  const openEdit = (v) => {
    setEditing(v);
    setFormData({
      docDate: v.docDate || today(),
      partyId: v.partyId || '', partyName: v.partyName || '',
      partyAccountId: v.partyAccountId || '', partyAccountCode: v.partyAccountCode || '',
      paymentMethod: v.paymentMethod || 'cash',
      paymentAccountId: v.paymentAccountId || '', paymentAccountCode: v.paymentAccountCode || '',
      paymentAccountName: v.paymentAccountName || '',
      reference: v.reference || '', notes: v.notes || '',
      status: v.status || 'Draft'
    });
    setViewMode('form');
    loadRows(v.partyId, v);
  };

  const goBack = () => { setViewMode('list'); resetForm(); };

  useOpenFromReport(vouchers, openEdit);

  // "Recover" from the Staff Loan / Salary Advance list: open a new voucher for
  // that staff with that loan's / advance's balance filled in.
  const presetDone = useRef(false);
  useEffect(() => {
    const preset = location.state?.recoverFor;
    if (!preset || presetDone.current || staffList.length === 0) return;
    presetDone.current = true;
    navigate(location.pathname, { replace: true, state: null });
    if (canEdit) openNew(preset);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, staffList]);

  const handleAccountChange = (accountId) => {
    const list = formData.paymentMethod === 'cash' ? cashAccounts : bankAccounts;
    const acc = list.find(a => a.id === accountId);
    setFormData(prev => ({ ...prev, paymentAccountId: accountId, paymentAccountCode: acc?.code || '', paymentAccountName: acc?.name || '' }));
  };
  const handlePaymentMethodChange = (method) => {
    setFormData(prev => ({ ...prev, paymentMethod: method, paymentAccountId: '', paymentAccountCode: '', paymentAccountName: '' }));
  };
  const setRowAmount = (id, value) => {
    setRows(prev => prev.map(r => (r.transactionId === id ? { ...r, amount: value } : r)));
  };

  // ─── Save ───
  const validate = () => {
    if (!formData.partyId) return 'Select the staff member who is paying back.';
    if (!formData.partyAccountId) return 'Selected staff has no linked account. Edit the staff record and save it again.';
    if (!formData.paymentAccountId) return `Select the ${formData.paymentMethod === 'cash' ? 'cash' : 'bank'} account the money was received in.`;
    if (rows.some(r => (Number(r.amount) || 0) < 0)) return 'Amounts can not be negative.';
    const over = rows.find(r => r2(r.amount) > r.available + 0.01);
    if (over) return `${over.docNumber}: only ${formatCurrency(over.available)} is left — the amount can't be more than that.`;
    if (total <= 0) return 'Enter the amount received against at least one loan or advance.';
    return null;
  };

  const buildData = (status) => ({
    tranTypeCode: LOAN_RECOVERY_CODE,
    docDate: formData.docDate,
    financialYearId: activeFinancialYear?.id,
    branchId: editing ? (editing.branchId || null) : (activeBranch?.id || null),
    branchName: editing ? (editing.branchName || null) : (activeBranch?.branchName || null),
    partyType: 'staff',
    partyId: formData.partyId,
    partyName: formData.partyName,
    partyAccountId: formData.partyAccountId,
    partyAccountCode: formData.partyAccountCode,
    paymentMethod: formData.paymentMethod,
    paymentAccountId: formData.paymentAccountId,
    paymentAccountCode: formData.paymentAccountCode,
    paymentAccountName: formData.paymentAccountName,
    reference: formData.reference,
    notes: formData.notes,
    allocations: rows.filter(r => r2(r.amount) > 0).map(r => ({
      transactionId: r.transactionId,
      tranTypeCode: r.tranTypeCode,
      docNumber: r.docNumber,
      docDate: r.docDate || '',
      balanceBefore: r2(r.available),
      amount: r2(r.amount),
    })),
    subtotal: total, discountAmount: 0, taxableAmount: total, taxAmount: 0,
    whtRate: 0, whtAmount: 0, totalAmount: total,
    status
  });

  const handleSave = async (status = 'Draft') => {
    if (!canEdit) { alert('No permission to save.'); return; }
    const err = validate();
    if (err) { alert(err); return; }
    if (isFutureDate(formData.docDate) && !window.confirm('Document date is in the future. Continue?')) return;
    setSaving(true);
    try {
      const data = buildData(status);
      const fresh = await checkAllocations(activeCompany.id, data, editing);
      if (fresh) { alert(fresh); return; }
      let saved;
      if (editing) {
        await updateTransaction(activeCompany.id, editing.id, data, currentUser.uid);
        saved = { ...editing, ...data, id: editing.id };
      } else {
        const created = await createTransaction(activeCompany.id, data, currentUser.uid);
        saved = { ...created, ...data, id: created.id, docNumber: created.docNumber };
      }
      try {
        await syncRecoveryEffects(activeCompany.id, editing, saved);
      } catch (e) {
        console.error('Loan balance update failed:', e);
        alert(`Voucher saved, but the loan / advance balance could not be updated (${e?.message || 'error'}). Open the loan / advance and use "Recalculate recovery".`);
      }
      goBack();
      loadVouchers().catch(() => {});
    } catch (e) {
      console.error('Save LRC error:', e);
      alert('Failed: ' + (e?.message || ''));
    } finally { setSaving(false); }
  };

  const handleStatusChange = async (v, newStatus) => {
    if (!canEdit) { alert('No permission.'); return; }
    const msg = newStatus === 'Cancelled'
      ? `Cancel ${v.docNumber}?\n\nThe ${formatCurrency(v.totalAmount || 0)} goes back onto the staff's loan / advance balance and the journal entry is reversed.`
      : `Post ${v.docNumber}? The staff's loan / advance balance comes down by ${formatCurrency(v.totalAmount || 0)}.`;
    if (!window.confirm(msg)) return;
    try {
      if (newStatus === 'Posted') {
        const fresh = await checkAllocations(activeCompany.id, { ...v, status: 'Posted' }, null);
        if (fresh) { alert(fresh); return; }
      }
      await updateTransactionStatus(activeCompany.id, v.id, newStatus, currentUser.uid);
      await syncRecoveryEffects(activeCompany.id, v, { ...v, status: newStatus });
      await loadVouchers();
    } catch (e) { alert('Failed: ' + (e?.message || '')); }
  };

  const handleDelete = async (v) => {
    if (!canDelete) { alert('No permission.'); return; }
    const msg = v.status === 'Posted'
      ? `Delete ${v.docNumber}?\n\nThe ${formatCurrency(v.totalAmount || 0)} goes back onto the staff's loan / advance balance and the journal entry is removed.`
      : `Delete ${v.docNumber}?`;
    if (!window.confirm(msg)) return;
    try {
      await deleteTransaction(activeCompany.id, v.id);
      await syncRecoveryEffects(activeCompany.id, v, null);
      await loadVouchers();
    } catch (e) { alert('Failed: ' + (e?.message || '')); }
  };

  useDocumentShortcuts({
    onNew: () => openNew(),
    onSave: () => { if (!readOnly) handleSave(editing?.status === 'Posted' ? 'Posted' : 'Draft'); },
    viewMode,
    saving
  });

  const filtered = vouchers.filter(v => {
    const q = searchQuery.toLowerCase();
    const m = !q
      || v.docNumber?.toLowerCase().includes(q)
      || v.partyName?.toLowerCase().includes(q)
      || v.reference?.toLowerCase().includes(q)
      || (v.allocations || []).some(a => a.docNumber?.toLowerCase().includes(q));
    const s = statusFilter === 'All' || v.status === statusFilter;
    return m && s;
  });

  // ═══════════════════════════════════════════════════════════ LIST
  if (viewMode === 'list') {
    const postedTotal = vouchers.filter(v => v.status === 'Posted').reduce((s, v) => s + (Number(v.totalAmount) || 0), 0);
    return (
      <PageLayout pageTitle="Loan Recovery" breadcrumb="Salary / Loan Recovery">
        <ActionBar>
          <SearchBox>
            <FiSearch />
            <input type="text" placeholder="Search doc#, staff, loan no..."
              value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
          </SearchBox>
          <FilterGroup>
            <StatusFilterSelect value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
              <option value="All">All Status</option>
              {Object.keys(STATUS_COLORS).map(s => <option key={s} value={s}>{s}</option>)}
            </StatusFilterSelect>
          </FilterGroup>
          {canEdit && <TranAddButton onClick={() => openNew()}><FiPlus /> New Loan Recovery</TranAddButton>}
        </ActionBar>

        <StatsRow>
          <StatCard><StatLabel>Vouchers</StatLabel><StatValue>{vouchers.length}</StatValue></StatCard>
          <StatCard $accent><StatLabel>Total Received (Posted)</StatLabel><StatValue>{formatCurrency(postedTotal)}</StatValue></StatCard>
        </StatsRow>

        {loading ? <LoadingMessage>Loading...</LoadingMessage>
          : filtered.length === 0 ? (
            <TranEmptyState>
              {searchQuery || statusFilter !== 'All'
                ? 'No vouchers match your filters.'
                : 'No loan recoveries yet. When a staff member pays a loan or advance back in cash or bank, record it here.'}
            </TranEmptyState>
          ) : (
            <TableContainer>
              <Table>
                <thead><tr>
                  <Th>Doc #</Th><Th>Date</Th><Th>Staff</Th><Th>Against</Th>
                  <Th>Received In</Th>
                  <Th style={{ textAlign: 'right' }}>Amount</Th>
                  {showBranchCol && <Th>Branch</Th>}
                  <Th>Status</Th>
                  <Th>Actions</Th>
                </tr></thead>
                <tbody>
                  {filtered.map(v => (
                    <Tr key={v.id}>
                      <Td><strong>{v.docNumber}</strong></Td>
                      <Td>{v.docDate}</Td>
                      <Td>{v.partyName || '-'}</Td>
                      <Td style={{ fontSize: 12 }}>{(v.allocations || []).map(a => a.docNumber).join(', ') || '-'}</Td>
                      <Td>
                        <PayBadge $type={v.paymentMethod}>{(v.paymentMethod || 'cash').toUpperCase()}</PayBadge>
                        <small style={{ marginLeft: 4, color: colors.text.secondary }}>{v.paymentAccountName || ''}</small>
                      </Td>
                      <Td style={{ textAlign: 'right', fontWeight: 600 }}>{formatCurrency(v.totalAmount || 0)}</Td>
                      {showBranchCol && <Td>{v.branchName || '-'}</Td>}
                      <Td>
                        <StatusBadge style={{ background: STATUS_COLORS[v.status] + '20', color: STATUS_COLORS[v.status] }}>{v.status}</StatusBadge>
                      </Td>
                      <Td>
                        <ActionGroup>
                          <IconBtn title="View" onClick={() => openEdit(v)}><FiEye /></IconBtn>
                          {canEdit && v.status !== 'Cancelled' && (
                            <IconBtn title="Edit" onClick={() => openEdit(v)}><FiEdit2 /></IconBtn>
                          )}
                          {canEdit && v.status === 'Draft' && (
                            <IconBtn title="Post" onClick={() => handleStatusChange(v, 'Posted')}><FiCheck /></IconBtn>
                          )}
                          {canEdit && v.status === 'Posted' && (
                            <IconBtn title="Cancel" onClick={() => handleStatusChange(v, 'Cancelled')}><FiXCircle /></IconBtn>
                          )}
                          <IconBtn title="Print" onClick={() => handlePrint(v)}><FiPrinter /></IconBtn>
                          {canDelete && (
                            <IconBtn $danger title="Delete" onClick={() => handleDelete(v)}><FiTrash2 /></IconBtn>
                          )}
                        </ActionGroup>
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            </TableContainer>
          )}
      </PageLayout>
    );
  }

  // ═══════════════════════════════════════════════════════════ FORM
  const accountList = formData.paymentMethod === 'cash' ? cashAccounts : bankAccounts;
  const staffChoices = staffList.filter(s => s.isActive !== false || s.id === formData.partyId);

  return (
    <PageLayout pageTitle="Loan Recovery" breadcrumb="Salary / Loan Recovery">
      <FormHeader>
        <BackButton onClick={goBack}><FiArrowLeft /> Back</BackButton>
        <FormTitle>{editing ? `${readOnly ? 'View' : 'Edit'} ${editing.docNumber}` : 'New Loan Recovery'}</FormTitle>
        <FormActions>
          {editing && (
            <SaveButton type="button" onClick={() => handlePrint(editing)} style={{ background: colors.text.secondary }}>
              <FiPrinter /> Print
            </SaveButton>
          )}
          {canEdit && !readOnly && (!editing || editing.status === 'Draft') && (
            <SaveButton type="button" onClick={() => handleSave('Draft')} disabled={saving}>Save Draft</SaveButton>
          )}
          {canEdit && !readOnly && (
            <SaveButton type="button" onClick={() => handleSave('Posted')} disabled={saving} style={{ background: colors.success.main }}>
              {saving ? 'Saving...' : (editing && editing.status === 'Posted' ? 'Save' : 'Save & Post')}
            </SaveButton>
          )}
        </FormActions>
      </FormHeader>

      <FormCard>
        <SectionTitle>Received From Staff</SectionTitle>
        <FormGrid>
          <FormGroup>
            <Label>Date *</Label>
            <Input type="date" value={formData.docDate} disabled={readOnly}
              onChange={e => setFormData({ ...formData, docDate: e.target.value })} />
          </FormGroup>
          <FormGroup>
            <Label>Salary Staff *</Label>
            <Select value={formData.partyId} disabled={readOnly || !!editing} onChange={e => pickStaff(e.target.value)}>
              <option value="">— Select Staff —</option>
              {staffChoices.map(s => (
                <option key={s.id} value={s.id}>{s.staffCode} — {s.fullName}</option>
              ))}
            </Select>
          </FormGroup>
          <FormGroup>
            <Label>Received In</Label>
            <RadioRow>
              <label>
                <input type="radio" name="recvMethod" checked={formData.paymentMethod === 'cash'} disabled={readOnly}
                  onChange={() => handlePaymentMethodChange('cash')} /> Cash
              </label>
              <label>
                <input type="radio" name="recvMethod" checked={formData.paymentMethod === 'bank'} disabled={readOnly}
                  onChange={() => handlePaymentMethodChange('bank')} /> Bank
              </label>
            </RadioRow>
          </FormGroup>
          <FormGroup>
            <Label>{formData.paymentMethod === 'cash' ? 'Cash Account' : 'Bank Account'} *</Label>
            <Select value={formData.paymentAccountId} disabled={readOnly} onChange={e => handleAccountChange(e.target.value)}>
              <option value="">— Select {formData.paymentMethod === 'cash' ? 'Cash' : 'Bank'} Account —</option>
              {accountList.map(a => (
                <option key={a.id} value={a.id}>{a.code} — {a.name}</option>
              ))}
            </Select>
          </FormGroup>
          <FormGroup style={{ gridColumn: 'span 2' }}>
            <Label>Reference</Label>
            <Input value={formData.reference} disabled={readOnly} placeholder="e.g. cheque no. / receipt no."
              onChange={e => setFormData({ ...formData, reference: e.target.value })} />
          </FormGroup>
          <FormGroup style={{ gridColumn: 'span 2' }}>
            <Label>Remarks</Label>
            <TextArea rows={1} value={formData.notes} disabled={readOnly}
              onChange={e => setFormData({ ...formData, notes: e.target.value })} />
          </FormGroup>
        </FormGrid>
      </FormCard>

      <FormCard>
        <SectionTitle>Loans &amp; Advances of this Staff</SectionTitle>
        {!formData.partyId ? (
          <EmptyNote>Select the staff member to see their loans and advances.</EmptyNote>
        ) : rowsLoading ? (
          <EmptyNote>Loading balances...</EmptyNote>
        ) : rows.length === 0 ? (
          <EmptyNote>This staff member has no loan or advance left to recover.</EmptyNote>
        ) : (
          <TableContainer>
            <Table>
              <thead><tr>
                <Th>Loan / Advance</Th><Th>Type</Th><Th>Date</Th>
                <Th style={{ textAlign: 'right' }}>Given</Th>
                <Th style={{ textAlign: 'right' }}>Balance</Th>
                <Th style={{ textAlign: 'right', width: 180 }}>Received Now</Th>
                <Th style={{ textAlign: 'right' }}>Balance After</Th>
                <Th></Th>
              </tr></thead>
              <tbody>
                {rows.map(r => (
                  <Tr key={r.transactionId}>
                    <Td><strong>{r.docNumber}</strong></Td>
                    <Td>{kindLabel(r.tranTypeCode)}</Td>
                    <Td>{r.docDate || '-'}</Td>
                    <Td style={{ textAlign: 'right' }}>{r.totalAmount ? formatCurrency(r.totalAmount) : '-'}</Td>
                    <Td style={{ textAlign: 'right', fontWeight: 600 }}>{formatCurrency(r.available)}</Td>
                    <Td style={{ textAlign: 'right' }}>
                      <Input type="number" min="0" step="0.01" value={r.amount} disabled={readOnly}
                        aria-label={`Received now ${r.docNumber}`}
                        style={{ textAlign: 'right' }}
                        onChange={e => setRowAmount(r.transactionId, e.target.value)} />
                    </Td>
                    <Td style={{ textAlign: 'right', color: colors.text.secondary }}>
                      {formatCurrency(Math.max(0, r2(r.available - (Number(r.amount) || 0))))}
                    </Td>
                    <Td>
                      {!readOnly && (
                        <SmallBtn type="button" onClick={() => setRowAmount(r.transactionId, r.available)}>Full</SmallBtn>
                      )}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </TableContainer>
        )}
      </FormCard>

      <SummaryCard>
        <SummaryItem><span>Staff</span><strong>{formData.partyName || '—'}</strong></SummaryItem>
        <SummaryItem><span>Received In</span><strong>{formData.paymentAccountName || '—'}</strong></SummaryItem>
        <SummaryItem $highlight><span>Total Received</span><strong>{formatCurrency(total)}</strong></SummaryItem>
      </SummaryCard>

      <InfoNote>
        <strong>How it works:</strong> when posted, the system records <code>Dr {formData.paymentMethod === 'cash' ? 'Cash' : 'Bank'} / Cr Staff Account</code> and
        the balance of each loan / advance above comes down. A loan paid off is closed and payroll stops deducting it; a part payment keeps the same
        monthly installment, so the loan finishes sooner. Cancelling or deleting the voucher puts the balance back.
      </InfoNote>
    </PageLayout>
  );
};

// ─── Local Styles ───
const RadioRow = styled.div`
  display: flex; gap: ${spacing.lg}; align-items: center;
  padding: ${spacing.sm} ${spacing.md};
  border: 1px solid ${colors.border.light}; border-radius: ${borderRadius.sm};
  background: ${colors.background.default};
  label { display: flex; align-items: center; gap: 6px; cursor: pointer; font-size: ${typography.fontSize.sm}; }
  input { cursor: pointer; }
`;
const PayBadge = styled.span`
  display: inline-block; padding: 2px 8px;
  background: ${props => props.$type === 'bank' ? colors.info.main + '20' : colors.warning.main + '20'};
  color: ${props => props.$type === 'bank' ? colors.info.dark : colors.warning.dark};
  border-radius: ${borderRadius.sm}; font-size: 10px; font-weight: 700;
`;
const StatsRow = styled.div`
  display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${spacing.md}; margin-bottom: ${spacing.md};
`;
const StatCard = styled.div`
  padding: ${spacing.md};
  background: ${props => props.$accent ? colors.primary.main : 'white'};
  color: ${props => props.$accent ? 'white' : colors.text.primary};
  border-radius: ${borderRadius.md};
  border: 1px solid ${colors.border.light};
`;
const StatLabel = styled.div`font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; opacity: 0.85;`;
const StatValue = styled.div`font-size: ${typography.fontSize.xl}; font-weight: 700; margin-top: 4px;`;
const EmptyNote = styled.div`padding: ${spacing.md}; color: ${colors.text.secondary}; font-size: ${typography.fontSize.sm};`;
const SmallBtn = styled.button`
  padding: 4px 10px; font-size: 12px; font-weight: 600;
  border: 1px solid ${colors.primary.main}; color: ${colors.primary.main};
  background: white; border-radius: ${borderRadius.sm}; cursor: pointer;
  &:hover { background: ${colors.primary.main}10; }
`;
const SummaryCard = styled.div`
  display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${spacing.md};
  padding: ${spacing.md};
  background: ${colors.neutral[50]};
  border-radius: ${borderRadius.lg};
  margin-top: ${spacing.md};
`;
const SummaryItem = styled.div`
  padding: ${spacing.sm} ${spacing.md};
  background: ${props => props.$highlight ? colors.primary.main : 'white'};
  color: ${props => props.$highlight ? 'white' : colors.text.primary};
  border-radius: ${borderRadius.sm};
  display: flex; flex-direction: column; gap: 4px;
  span { font-size: 11px; opacity: 0.85; text-transform: uppercase; letter-spacing: 0.5px; }
  strong { font-size: ${typography.fontSize.lg}; font-family: monospace; }
`;
const InfoNote = styled.div`
  margin-top: ${spacing.md};
  padding: ${spacing.md};
  background: ${colors.info.main}10;
  border-left: 3px solid ${colors.info.main};
  border-radius: ${borderRadius.sm};
  color: ${colors.text.secondary};
  font-size: 12px;
  line-height: 1.6;
  code {
    background: white; padding: 1px 6px;
    border-radius: 3px; font-family: monospace;
    color: ${colors.primary.main};
  }
`;

export default LoanRecovery;
